import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Eye, Users, TrendingUp } from "lucide-react";

const PAGE_LABELS = {
  "/": "Home",
  "/classes": "Classes & Camps",
  "/tutorials": "Vault Tutorials",
  "/studio": "Studio",
  "/about": "About",
  "/gift": "Gift Cards",
  "/membership": "Membership",
  "/open-studio": "Open Studio",
  "/parties": "Parties",
  "/shop": "Shop",
  "/cart": "Cart",
  "/creators": "Creators",
  "/login": "Login",
  "/register": "Register",
  "/dashboard": "Dashboard",
  "/admin": "Admin",
};
const labelFor = (p) => {
  if (PAGE_LABELS[p]) return PAGE_LABELS[p];
  if (p.startsWith("/classes/")) return "Class detail";
  if (p.startsWith("/tutorials/")) return "Tutorial detail";
  if (p.startsWith("/creators/")) return "Creator profile";
  return p;
};

const fmtDay = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString("default", { month: "short", day: "numeric" });

export default function AdminTraffic() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/admin/analytics?days=${days}`)
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [days]);

  if (loading && !data)
    return <p data-testid="traffic-loading" className="mt-10 text-stone-500 text-sm">Loading traffic…</p>;
  if (!data) return <p className="mt-10 text-stone-500 text-sm">Couldn’t load traffic data.</p>;

  const series = data.views_per_day || [];
  const max = Math.max(1, ...series.map((d) => d.views));
  const stat = [
    { l: `Views · last ${data.days}d`, v: data.total_views, Icon: Eye },
    { l: `Unique visitors · ${data.days}d`, v: data.unique_visitors, Icon: Users },
    { l: "Views today", v: data.today_views, Icon: TrendingUp },
    { l: "Visitors today", v: data.today_visitors, Icon: Users },
  ];

  return (
    <div data-testid="admin-traffic" className="mt-10 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-xs tracking-[0.2em] uppercase text-stone-500">Website traffic</p>
        <div className="inline-flex rounded-full border border-stone-300 overflow-hidden">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`traffic-range-${d}`}
              onClick={() => setDays(d)}
              className={`px-4 py-1.5 text-sm transition-colors ${
                days === d ? "bg-[#121212] text-white" : "text-stone-600 hover:bg-stone-100"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stat.map(({ l, v, Icon }) => (
          <div key={l} className="p-6 rounded-2xl bg-white border border-stone-200">
            <Icon size={18} className="text-[#E0218A]" />
            <p className="text-xs tracking-[0.2em] uppercase text-stone-500 mt-3">{l}</p>
            <p className="font-serif font-black text-4xl text-[#121212] mt-1">{v ?? 0}</p>
          </div>
        ))}
      </div>

      <div data-testid="traffic-chart" className="p-6 rounded-2xl bg-white border border-stone-200">
        <p className="text-xs tracking-[0.2em] uppercase text-stone-500">Page views per day</p>
        {series.every((d) => d.views === 0) ? (
          <p className="text-stone-500 text-sm mt-6">
            No page views recorded yet — visits will chart here automatically as people browse your site.
          </p>
        ) : (
          <>
            <div className="mt-6 flex items-end gap-[3px] h-40">
              {series.map((d) => (
                <div
                  key={d.date}
                  className="group relative flex-1 flex flex-col justify-end h-full"
                  title={`${fmtDay(d.date)} · ${d.views} views`}
                >
                  <div
                    className="w-full bg-[#C6FF33] hover:bg-[#121212] transition-colors rounded-sm"
                    style={{ height: `${Math.max(2, (d.views / max) * 100)}%` }}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-[10px] text-stone-400">
              <span>{fmtDay(series[0].date)}</span>
              <span>{fmtDay(series[series.length - 1].date)}</span>
            </div>
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div data-testid="traffic-top-pages" className="p-6 rounded-2xl bg-white border border-stone-200">
          <p className="text-xs tracking-[0.2em] uppercase text-stone-500 mb-4">Top pages</p>
          {(data.top_pages || []).length === 0 ? (
            <p className="text-stone-500 text-sm">No data yet.</p>
          ) : (
            <div className="space-y-2">
              {data.top_pages.map((p) => (
                <div key={p.path} className="flex items-center justify-between text-sm">
                  <span className="text-stone-800 truncate pr-3">{labelFor(p.path)}</span>
                  <span className="font-bold text-[#121212] tabular-nums">{p.views}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div data-testid="traffic-top-referrers" className="p-6 rounded-2xl bg-white border border-stone-200">
          <p className="text-xs tracking-[0.2em] uppercase text-stone-500 mb-4">Traffic sources</p>
          {(data.top_referrers || []).length === 0 ? (
            <p className="text-stone-500 text-sm">No data yet.</p>
          ) : (
            <div className="space-y-2">
              {data.top_referrers.map((r) => (
                <div key={r.source} className="flex items-center justify-between text-sm">
                  <span className="text-stone-800 truncate pr-3">{r.source}</span>
                  <span className="font-bold text-[#121212] tabular-nums">{r.views}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
